<template>
  <div :id="$attrs['id']" :class="[[$attrs['class'], gridClasses], { 'hm-table--scrollable': scrollable }, { 'hm-table--hover': hover}, {'hm-table--condensed': condensed}, {'hm-table--displaylastline': displayLastLine}]" class="hm-table">
    <resize-sensor @resize="onresize"></resize-sensor>
    <div :class="[{ 'hm-table__content--sticky' : sticky}, { 'hm-table__content--draggable' : draggable}]" :style="style" :key="key" class="hm-table__content">
      <slot name="table-head" />
      <slot />
      <slot name="table-foot" />
    </div>
  </div>
</template>

<script>

import ResizeSensor from 'vue-resize-sensor';
import { split, endsWith, head, isNil, isEmpty } from 'lodash';
import GridMixin from '@/mixins/GridMixin';
import UserConfigMixin from '../../../mixins/UserConfigMixin';

export default {
  name: 'HmTable',

  components: {
    'resize-sensor': ResizeSensor,
  },

  mixins: [GridMixin, UserConfigMixin],

  props: {
    sticky: {
      required: false,
      type: Boolean,
      default: false,
    },
    columns: {
      required: true,
      type: String,
      default: '1',
    },
    gridTemplateColumns: {
      required: false,
      type: String,
      default: null,
    },
    minWidth: {
      required: false,
      type: String,
      default: null,
    },
    hover: {
      required: false,
      type: Boolean,
      default: true,
    },
    draggable: {
      required: false,
      type: Boolean,
      default: false,
    },
    displayLastLine: {
      required: false,
      type: Boolean,
      default: false,
    },
    disableScroll: {
      required: false,
      type: Boolean,
      default: false,
    },
    dense: {
      required: false,
      type: Boolean,
      default: false,
    },
  },

  data: function () {
    return {
      innerWidth: 0,
      parentWidth: 0,
      condensed: false,
    };
  },

  computed: {
    key: function () {
      if (!isNil(this.$slots) && !isNil(this.$slots.default)) {
        return this.$slots.default.length;
      } else {
        return 0;
      }
    },
    scrollable: function () {
      if (this.disableScroll === true) return false;
      if (this.sticky === false) return true;
      if (!isNil(this.minWidth)) {
        if (this.minWidth.match(/^[0-9]+px$/gi)) {
          try {
            const minWidthPx = this.minWidth.matchAll(/^([0-9]+)px$/gi).next().value[1];

            return this.innerWidth <= minWidthPx || this.innerWidth > this.parentWidth;
            // eslint-disable-next-line no-empty
          } catch (error) { }

          return true;
        } else {
          return true;
        }
      } else {
        return false;
      }
    },
    currentColumns: function () {
      let ret = head(split(this.columns, ' ')
        .filter(it => endsWith(it, `@${this.$store.getters['layout/activeBreakpoint']}`))
        .map(it => split(it, '@')[0]));

      if (isNil(ret)) {
        ret = head(split(this.columns, ' ')
          .filter(it => it.indexOf('@') < 0));
      }

      return isNil(ret) ? 1 : ret;
    },
    style: function () {
      const ret = {
        '--table-columns': this.currentColumns,
        '--table-columns-1': this.currentColumns - 1,
      };

      if (!isEmpty(this.gridTemplateColumns)) {
        ret['grid-template-columns'] = this.gridTemplateColumns;
      }

      if (!isNil(this.minWidth)) {
        ret['min-width'] = `${this.minWidth}`;
      }

      return ret;
    },
  },

  created: function () {
    this.condensed = JSON.parse(this.getUserConfig('condensedTables', true)) || false;
    if (this.dense) this.condensed = true;
    this.$serviceBus.$on('update:condensedTables', this.updateCondensed);
  },

  beforeDestroy() {
    this.$serviceBus.$off('update:condensedTables', this.updateCondensed);
  },

  methods: {
    updateCondensed(newVal) {
      this.condensed = newVal;
    },
    onresize($event) {
      this.innerWidth = $event.width;
      this.parentWidth = isNil(this.$el.parentElement) ? 0 : this.$el.parentElement.clientWidth;
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
@import '~styles/import';
  .hm-table {
    display: grid;
    grid-template-columns: 100%;
    grid-column-end: span 12;
    background-color: $white;
    position: relative;

    &:after {
      content: ' ';
      position: absolute;
      bottom: 0px;
      left: 0;
      right: 0;
      height: 1px;
      background: #fff;
      z-index: 1;
    }

    &--displaylastline {
      &:after {
        display: none;
      }
    }

    &--hover {
      cursor: pointer;

      .hm-table {
        &__td {
          &--no-hover {
            cursor: default;
            &:hover:before {
              display: none;
            }
          }

          &:hover:before {
            content: ' ';
            left: 0;
            right: 0;
            background: $lightmint!important;
            height: 4.8rem;
            position: absolute;
            z-index: -1;
          }
        }
      }
    }
    &--scrollable {
      overflow: auto;
    }
    &__content {
      display: grid;
      grid-template-columns: repeat(var(--table-columns), auto);
      grid-auto-rows: max-content; // otherwise auto rows might grow so large that the row is in the middle of the table if the table has a min-height set
      width: 100%;
      z-index: 1;

      &--draggable {
        grid-template-columns: 3.5rem repeat(var(--table-columns-1), auto);
      }

      > div {
        white-space: nowrap;
        padding: 0 1.5rem;

        &.hm-table__td {
          &--input {
            padding: 0 1rem 0 0.75rem;
          }
        }
      }
      &--sticky {
        .hm-table__th {
          position: sticky;
          top: 0px;
          z-index: 10;
        }
      }
    }

    &__td {
      display: block;
      height: 4.8rem;
      line-height: 4.8rem;
      border-bottom: 1px dashed $middlegray;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      user-select: none;

      &--overflow {
        overflow: visible!important;
      }
      &--drop:before {
        content: ' ';
        left: 0;
        right: 0;
        background: $lightmint!important;
        height: 4.8rem;
        position: absolute;
        z-index: -1;
      }

      &.reset-line-height {
        line-height: inherit;
        padding-top: calc((4.8rem - 1.15 * 1.4rem) / 2);
        padding-bottom: calc((4.8rem - 1.15 * 1.4rem) / 2);
        height: auto;
      }
      &--clickable {
        cursor: pointer;

        &:hover {
          color: $mint;
        }
      }
      &--has-child-rows {
        border-bottom: 1px solid transparent!important;
        height: 35px;
      }
      &--th {
        font-weight: bold;
        text-transform: uppercase;
      }
      &--icon {
        display: grid !important;
        grid-template-rows: 1fr;
        grid-auto-columns: 4.7rem;
        padding-left: 0;
        justify-items: right;
        justify-content: end;

        /deep/ .quick-action-button, /deep/ .icon-wrapper {
          position: relative;
          grid-row: 1;
          display: flex;
          height: 100%;
          min-width: 4.7rem;
          width: 100%;
          max-width: 4.7rem;
          overflow: hidden;
          cursor: pointer;
          padding: 0.5rem;

          .hm-icon {
            display: flex;
            flex-direction: column;
            flex: 1;

            svg {
              width: 100%;
              height: 100%;
            }
          }
        }

        @include icon-color($middlegray);
        &:not(.hm-table__td--icon--no-hover) {
          @include icon-hover($mint);
        }

      }
    }

    &__th {
      // display: flex; // fixes https://bugs.chromium.org/p/chromium/issues/detail?id=995576
      flex: 1;
      flex-direction: column;
      font-weight: bold;
      line-height: 4.8rem;
      text-transform: uppercase;
      height: 4.8rem;
      background-color: $white;
      border-bottom: 3px double $middlegray;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      cursor: default!important;

      &.text-right {
        justify-content: right;
        justify-items: right;
      }

      --fa-primary-opacity: 1.0;
      --fa-secondary-opacity: 1.0;

      --fa-primary-color: #{$middlegray};
      --fa-secondary-color: #{$middlegray};

      &--sortable {
        cursor: pointer!important;

        /deep/ .fa-sort, /deep/ .fa-sort-down, /deep/ .fa-sort-up {
          margin-left: 1rem;
        }
      }

      &--asc {
        --fa-primary-color: #{$gray};
      }

      &--desc {
        --fa-secondary-color: #{$gray};
      }
    }

    &--condensed {
      &.hm-table--hover {
        .hm-table {
          &__td {
            &:hover:before {
              height: 35px!important;
            }
            &--drop:before {
              height: 35px!important;
            }
          }
        }
      }
      .hm-table {

        &__td {
          height: 35px;
          line-height: 35px;

          div[class*='component__hm-input'] {
            margin-top: -0.5rem;
          }

          &.reset-line-height {
            padding-top: calc((35px - 1.15 * 1.4rem) / 2);
            padding-bottom: calc((35px - 1.15 * 1.4rem) / 2);
            //padding-top: 0px;
            //padding-bottom: 0px;
            line-height: inherit;
            height: auto;
          }

          &.height-auto {
            height: auto;
          }

          &--icon {
            grid-auto-columns: 34px;

            .quick-action-button {
              padding: 0;
              min-width: 34px;
              max-width: 34px;
            }

            .icon-wrapper {
              padding: 0;
              min-width: 34px;
              max-width: 34px;
            }
          }

          &--has-child-rows {
            border-bottom: 1px solid transparent!important;
            height: 30px;
          }
        }

        &__th {
          line-height: 35px;
          height: 35px;
        }
      }
    }
  }
</style>

<style lang="scss">
  .hm-table__td {
    .hm-input__label {
      display: none;
    }

    &--icon {
      .quick-action-button, .icon-wrapper {
        .hm-icon {
          display: flex;
          flex-direction: column;
          flex: 1;

          svg {
            width: 100%;
            height: 100%;
          }
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/lib/table/HmTable.vue